/**
 * Хук серверного поиска по содержимому сообщений для строки поиска над
 * списком чатов.
 *
 * ЧТО ОН ГАРАНТИРУЕТ (прямо из жалоб Антона 24.09 — «молчит и ничего не
 * происходит»): у поиска ВСЕГДА есть видимое состояние. idle / too-short /
 * loading / empty / results / error. Пустого молчания быть не может.
 *
 * ЗАДЕРЖКА И ОТМЕНА: запрос уходит через DEBOUNCE_MS после последнего
 * нажатия клавиши; предыдущий запрос отменяется AbortController'ом. Ответ,
 * пришедший на устаревший запрос, отбрасывается по сравнению `runId` — одной
 * отмены мало, потому что fetch может успеть прорезаться до abort().
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { MatrixClient } from "matrix-js-sdk";
import {
  SEARCH_MIN_LEN,
  searchMessages,
  type MessageHit,
} from "../matrix/messageSearch";

/** Пауза после последнего нажатия клавиши перед запросом на сервер. */
const DEBOUNCE_MS = 350;

export type MessageSearchStatus =
  | "idle"
  | "short"
  | "loading"
  | "ready"
  | "error";

export interface MessageSearchState {
  status: MessageSearchStatus;
  hits: MessageHit[];
  /** Термины для подсветки (ответ сервера + сама строка запроса). */
  highlights: string[];
  /** Всего совпадений по данным сервера; undefined — сервер не сказал. */
  count?: number;
  /** Есть ли что догружать. */
  hasMore: boolean;
  /** Идёт догрузка следующей порции (первая порция — status==="loading"). */
  loadingMore: boolean;
  /** Текст ошибки для показа человеку; null — ошибки нет. */
  error: string | null;
  /** Запрос, к которому относится текущее состояние. */
  term: string;
  /** Догрузить следующую порцию. */
  loadMore: () => void;
  /** Повторить запрос после ошибки. */
  retry: () => void;
}

/** Человеческий текст ошибки из чего угодно, что прилетело в catch. */
function errorText(e: unknown): string {
  if (e && typeof e === "object") {
    const anyE = e as { errcode?: string; message?: string; name?: string };
    if (anyE.errcode) return `${anyE.errcode}${anyE.message ? `: ${anyE.message}` : ""}`;
    if (anyE.message) return anyE.message;
  }
  return String(e);
}

export function useMessageSearch(client: MatrixClient, query: string): MessageSearchState {
  const term = query.trim();
  const [state, setState] = useState<{
    status: MessageSearchStatus;
    hits: MessageHit[];
    highlights: string[];
    count?: number;
    nextBatch?: string;
    loadingMore: boolean;
    error: string | null;
    term: string;
  }>({
    status: "idle",
    hits: [],
    highlights: [],
    loadingMore: false,
    error: null,
    term: "",
  });

  /** Номер актуального «поколения» запроса: ответы старых поколений игнорируем. */
  const runIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  /** Счётчик ручных повторов — нужен, чтобы retry перезапускал тот же term. */
  const [retryTick, setRetryTick] = useState(0);

  // --- первая страница: debounce + отмена предыдущего ---
  useEffect(() => {
    abortRef.current?.abort();
    const runId = ++runIdRef.current;

    if (term.length === 0) {
      setState({ status: "idle", hits: [], highlights: [], loadingMore: false, error: null, term });
      return;
    }
    if (term.length < SEARCH_MIN_LEN) {
      setState({ status: "short", hits: [], highlights: [], loadingMore: false, error: null, term });
      return;
    }

    setState((prev) => ({
      ...prev,
      status: "loading",
      error: null,
      loadingMore: false,
      term,
      // Прошлые результаты НЕ стираем: при дописывании буквы список не
      // должен мигать пустотой — он просто обновится ответом.
    }));

    const timer = window.setTimeout(() => {
      const ac = new AbortController();
      abortRef.current = ac;
      searchMessages(client, term, { signal: ac.signal })
        .then((page) => {
          if (runIdRef.current !== runId) return;
          setState({
            status: "ready",
            hits: page.hits,
            highlights: page.highlights.length ? page.highlights : [term],
            count: page.count,
            nextBatch: page.nextBatch,
            loadingMore: false,
            error: null,
            term,
          });
        })
        .catch((e) => {
          if (runIdRef.current !== runId) return;
          if (ac.signal.aborted) return;
          setState({
            status: "error",
            hits: [],
            highlights: [],
            loadingMore: false,
            error: errorText(e),
            term,
          });
        });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [client, term, retryTick]);

  // Отмена висящего запроса при размонтировании.
  useEffect(() => () => abortRef.current?.abort(), []);

  const loadMore = useCallback(() => {
    setState((prev) => {
      if (!prev.nextBatch || prev.loadingMore) return prev;
      const runId = runIdRef.current;
      const ac = new AbortController();
      searchMessages(client, prev.term, { nextBatch: prev.nextBatch, signal: ac.signal })
        .then((page) => {
          if (runIdRef.current !== runId) return;
          setState((cur) => {
            if (cur.term !== prev.term) return cur;
            // Дедуп по eventId: сервер на границе страниц изредка повторяет
            // событие, а дубль в списке выглядит как потерянный результат.
            const seen = new Set(cur.hits.map((h) => h.eventId));
            const added = page.hits.filter((h) => !seen.has(h.eventId));
            return {
              ...cur,
              hits: [...cur.hits, ...added],
              nextBatch: page.nextBatch,
              count: page.count ?? cur.count,
              loadingMore: false,
              error: null,
            };
          });
        })
        .catch((e) => {
          if (runIdRef.current !== runId) return;
          if (ac.signal.aborted) return;
          // Уже показанное НЕ теряем — ошибка догрузки показывается под списком.
          setState((cur) => ({ ...cur, loadingMore: false, error: errorText(e) }));
        });
      return { ...prev, loadingMore: true, error: null };
    });
  }, [client]);

  const retry = useCallback(() => setRetryTick((v) => v + 1), []);

  return {
    status: state.status,
    hits: state.hits,
    highlights: state.highlights,
    count: state.count,
    hasMore: Boolean(state.nextBatch),
    loadingMore: state.loadingMore,
    error: state.error,
    term: state.term,
    loadMore,
    retry,
  };
}
